// src/components/ReportReminder.js
//
// "Submit your weekly report" pop-up for coordinators, on Fridays and on the
// last working day of the month (rules: services/reportReminderSchedule.js).
// Mounted globally in App.js next to TenantHeader.
//
// Shows only when ALL of these hold:
//   - the signed-in user's role on the active site is exactly "coordinator"
//     (not owners or admins)
//   - their 2FA session is valid (same otp_sessions check as RequireAuth), so
//     it never appears mid-login
//   - the page isn't a public/auth page or the report form itself
//
// Snooze / dismiss choices are kept per user in this browser
// (services/reportReminderStore.js).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { doc, getDoc } from "firebase/firestore";
import { AlarmClock, CalendarCheck, CheckCircle2, ClipboardList } from "lucide-react";
import { db } from "../firebase";
import { useTenant } from "../context/TenantContext";
import { effectiveRole } from "../services/membershipService";
import {
  applyChoice,
  effectiveReminderKind,
  msUntilNextCheck,
  reminderCopy,
  shouldShow,
  snoozeChoices,
} from "../services/reportReminderSchedule";
import { REPORT_REMINDER_EVERY_DAY } from "../config/featureFlags";
import { loadReminderState, saveReminderState } from "../services/reportReminderStore";

// Staging testing switch (never set for production): shows it every day.
const SCHEDULE_OPTS = { everyDay: REPORT_REMINDER_EVERY_DAY };

const HIDDEN_PATHS = ["/", "/login", "/set-password", "/change-password", "/weekly-survey"];
const HIDDEN_PREFIXES = ["/esign", "/referral-form", "/health-survey", "/monitor-view"];

const isHiddenPath = (path) =>
  HIDDEN_PATHS.includes(path) || HIDDEN_PREFIXES.some((p) => path.startsWith(p));

const ReportReminder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, access, tenantId, siteId, membershipLoading } = useTenant();
  const uid = user?.uid || null;
  const path = location.pathname;

  const isCoordinator =
    !!uid && !membershipLoading && effectiveRole(access, tenantId, siteId) === "coordinator";
  const hiddenHere = isHiddenPath(path);

  // 2FA session check. Re-run on navigation until it passes: right after the
  // OTP step, the session doc is written and the app navigates away from /login.
  const [otpOk, setOtpOk] = useState(false);
  useEffect(() => {
    setOtpOk(false);
  }, [uid]);
  useEffect(() => {
    if (!uid || otpOk || hiddenHere || !isCoordinator) return undefined;
    let cancelled = false;
    getDoc(doc(db, "otp_sessions", uid))
      .then((snap) => {
        const session = snap.exists() ? snap.data() : null;
        const valid = !!(session && session.expiresAt && Date.now() < session.expiresAt);
        if (!cancelled && valid) setOtpOk(true);
      })
      .catch(() => {
        /* no session readable -> no reminder */
      });
    return () => {
      cancelled = true;
    };
  }, [uid, otpOk, hiddenHere, isCoordinator, path]);

  // Per user AND per company + site (a coordinator at two sites reports for each).
  const [state, setState] = useState(() => loadReminderState(uid, tenantId, siteId));
  useEffect(() => {
    setState(loadReminderState(uid, tenantId, siteId));
  }, [uid, tenantId, siteId]);

  // `now` drives re-evaluation: a timer for the next snooze end or midnight,
  // plus a re-check whenever the tab regains focus (sleeping laptops delay timers).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const ms = msUntilNextCheck(state, now);
    const t = setTimeout(() => setNow(Date.now()), ms);
    return () => clearTimeout(t);
  }, [state, now]);
  useEffect(() => {
    const refresh = () => setNow(Date.now());
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const open = isCoordinator && otpOk && !hiddenHere && shouldShow(state, now, SCHEDULE_OPTS);
  const kind = effectiveReminderKind(now, SCHEDULE_OPTS);
  const copy = useMemo(() => reminderCopy(kind), [kind]);
  const snoozes = useMemo(() => snoozeChoices(now), [now]);

  const choose = useCallback(
    (choiceId) => {
      const t = Date.now();
      const next = applyChoice(state, choiceId, t);
      saveReminderState(uid, tenantId, siteId, next);
      setState(next);
      setNow(t);
      if (choiceId === "submitNow") navigate("/weekly-survey");
    },
    [state, uid, tenantId, siteId, navigate]
  );

  // Escape counts as "remind me in 1 hour", never as a silent dismiss.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") choose("hour");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, choose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="report-reminder-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            className="report-reminder-modal relative w-full max-w-sm rounded-2xl border border-purple-400/40 bg-slate-950/95 backdrop-blur-2xl shadow-[0_24px_60px_-12px_rgba(124,58,237,0.45)] overflow-hidden"
          >
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-80 pointer-events-none" />
            <div className="p-5">
              <div className="flex items-start gap-3">
                <div className="report-reminder-icon shrink-0 w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-400/30 flex items-center justify-center">
                  <CalendarCheck className="w-5 h-5 text-purple-300" />
                </div>
                <div className="min-w-0">
                  <h3 id="report-reminder-title" className="text-sm font-bold text-white">
                    {copy.title}
                  </h3>
                  <p className="mt-1 text-xs leading-relaxed text-slate-300">{copy.body}</p>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <button
                  type="button"
                  onClick={() => choose("submitNow")}
                  className="report-reminder-primary w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-sm font-semibold text-white shadow-[0_8px_24px_-8px_rgba(139,92,246,0.7)] hover:brightness-110 active:scale-[0.98] transition"
                >
                  <ClipboardList className="w-4 h-4" />
                  Submit my report now
                </button>

                <div className={`grid gap-2 ${snoozes.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                  {snoozes.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => choose(s.id)}
                      className="report-reminder-snooze inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-xs font-semibold text-slate-200 hover:bg-white/[0.08] hover:border-white/20 transition"
                    >
                      <AlarmClock className="w-3.5 h-3.5" />
                      {s.label}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => choose("submitted")}
                  className="report-reminder-done w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-400/30 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/20 transition"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  I've already submitted my report
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReportReminder;
